import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ENTERPRISE_LINKS } from "../enterpriseLinks";

const TRUST_STATS = [
  { value: 22, suffix: "+", label: "Empowered platforms" },
  { value: 9, label: "Modern rental APIs" },
  { value: 1, label: "Hub for everything rental" },
];

const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

function CountUpStat({ value, suffix = "", duration = 1400, className }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(value);
      return;
    }
    let frame = 0;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          setDisplay(Math.round(easeOutExpo(progress) * value));
          if (progress < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [value, duration]);

  return (
    <span ref={ref} className={className}>
      <span aria-hidden="true">
        {display}
        {suffix}
      </span>
      <span className="sr-only">
        {value}
        {suffix}
      </span>
    </span>
  );
}

const WORDS = Array.from({ length: 4 }, () => "rentid enterprise");

function WordmarkRow({ hidden = false }) {
  return (
    <div className="flex shrink-0 items-start" aria-hidden={hidden || undefined}>
      {WORDS.map((word, i) => (
        <span
          key={i}
          className="whitespace-nowrap bg-gradient-to-b from-foreground/10 to-foreground/[0.05] bg-clip-text px-[0.15em] font-heading font-bold leading-none tracking-tighter text-transparent"
        >
          {word}
        </span>
      ))}
    </div>
  );
}

/* The clip height (0.72em) keeps most of each glyph while trimming the bottom edge at the
   trust bar's top border. The small negative top margin keeps the ascenders clear of the hero CTAs. */
function EnterpriseWordmark() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none relative z-0 -mt-[0.06em] h-[0.72em] select-none overflow-hidden text-[clamp(6rem,18vw,15rem)]"
    >
      <div className="ent-marquee flex w-max">
        <WordmarkRow />
        <WordmarkRow hidden />
      </div>
    </div>
  );
}

export function EnterpriseHero() {
  return (
    <section className="relative border-b border-border">
      <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center gap-8 px-4 pt-20 pb-6 text-center sm:px-6 md:pt-28">
        <h1 className="font-heading text-5xl font-semibold leading-[0.95] tracking-tighter text-foreground text-balance sm:text-6xl lg:text-[60px]">
          Build fast with modern rental APIs for insurance, verification, tolls, and roadside.
        </h1>
        <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground text-pretty">
          Access infrastructure built for your rental platform
        </p>
        <div className="flex flex-col gap-3 pt-2 sm:flex-row">
          <Link
            to={ENTERPRISE_LINKS.getStarted}
            className="group inline-flex h-12 items-center justify-center gap-2 rounded-full ent-cta bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors"
          >
            Get Started
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
          <Link
            to={ENTERPRISE_LINKS.login}
            className="inline-flex h-12 items-center justify-center rounded-full border border-border bg-background px-6 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
          >
            Client Login
          </Link>
        </div>
      </div>

      <EnterpriseWordmark />

      <ul className="relative z-10 grid grid-cols-1 divide-y divide-border border-t border-border bg-background sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {TRUST_STATS.map((stat) => (
          <li key={stat.label} className="flex flex-col items-center gap-1 px-6 py-8 text-center">
            <CountUpStat
              value={stat.value}
              suffix={stat.suffix}
              className="font-heading text-4xl font-semibold tabular-nums tracking-tighter text-foreground"
            />
            <span className="text-sm leading-relaxed text-muted-foreground">{stat.label}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
